"use client";

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import type { User } from "firebase/auth";
import { useRouter } from "next/navigation";
import { AppAuthContext, type AppAuthState } from "@/hooks/useAppAuth";
import { API_URL } from "@/lib/api-config";

export default function AppAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [firebaseLoaded, setFirebaseLoaded] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [hasMobile, setHasMobile] = useState<boolean | null>(null);
  const [picture, setPicture] = useState<string | undefined>(undefined);
  const [userName, setUserName] = useState<string | null>(null);
  const [rankFrame, setRankFrame] = useState<string>("/assets/rank/1.webp");
  const [authError, setAuthError] = useState<string | null>(null);

  const isLoadingProfile = useRef<boolean>(false);
  const lastSuccessLoad = useRef<number>(0);
  const isRedirectChecking = useRef<boolean>(false);
  const firebaseUserRef = useRef<User | null>(null);

  // Rank XP thresholds
  const RANK_XP_THRESHOLDS = [0, 500, 1000, 2000, 5000, 10000];

  const logout = useCallback(async () => {
    try {
      const { signOut } = await import("firebase/auth");
      const { auth } = await import("@/lib/firebase");
      await signOut(auth);
    } catch {
      // ignore
    }

    if (typeof window !== "undefined") {
      // 1. Clear Storage
      localStorage.clear();
      sessionStorage.clear();

      // 2. Clear All Cookies
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        // Also try clearing from subdomains if necessary, but path=/ is standard
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`;
      }

      // 3. Clear IndexedDB (aggressive)
      if (window.indexedDB && window.indexedDB.databases) {
        try {
          const dbs = await window.indexedDB.databases();
          dbs.forEach((db) => {
            if (db.name) window.indexedDB.deleteDatabase(db.name);
          });
        } catch (e) {
          console.warn("[AUTH] Error clearing IndexedDB:", e);
        }
      }
    }

    // 4. Reset Local State
    setFirebaseUser(null);
    firebaseUserRef.current = null;
    setUserGroups([]);
    setPicture(undefined);
    setUserName(null);
    setHasMobile(null);
    setRankFrame("/assets/rank/1.webp");
    lastSuccessLoad.current = 0;
    isLoadingProfile.current = false;

    // 5. Hard Redirect to home to purge any remaining memory state
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }, []);

  /**
   * After Firebase confirms a user, fetch their extended profile from our API.
   * Uses the Firebase ID token as Bearer auth.
   */
  const loadUserProfile = useCallback(
    async (user: User, isRetry = false) => {
      const now = Date.now();

      if (isLoadingProfile.current && !isRetry) return;
      if (!isRetry && now - lastSuccessLoad.current < 15000) return;

      isLoadingProfile.current = true;

      try {
        // Get a fresh ID token (Firebase auto-refreshes when needed)
        const idToken = await user.getIdToken();

        const apiBase = API_URL;

        const authHeader = `Bearer ${idToken}`;

        const dbRes = await fetch(`${apiBase}/public/user/profile`, {
          headers: { Authorization: authHeader },
          cache: "no-store",
        }).catch(() => null);

        // Handle 401 - token rejected (shouldn't happen with fresh token, but guard anyway)
        if (dbRes?.status === 401) {
          console.warn("[AUTH] Token rejected by backend - refreshing");
          try {
            await user.getIdToken(true); // force refresh
            isLoadingProfile.current = false;
            await loadUserProfile(user, true);
          } catch {
            logout();
          }
          return;
        }

        lastSuccessLoad.current = Date.now();

        let dbData: any = null;
        if (dbRes?.ok) dbData = await dbRes.json().catch(() => null);

        // Extract groups/role from Firebase custom claims in the decoded token.
        // Force refresh to ensure custom role claims set by
        // backend during /public/user/profile are immediately available.
        const tokenResult = await user.getIdTokenResult(true);
        const claims = tokenResult.claims || {};
        const groups: string[] = [];
        if (claims.role) groups.push(String(claims.role).toLowerCase());
        if (Array.isArray(claims.roles)) {
          groups.push(...claims.roles.map((r: any) => String(r).toLowerCase()));
        }
        setUserGroups(Array.from(new Set(groups)));

        // Mobile status from DB profile
        const profile = dbData?.data || dbData;
        if (profile?.mobile) {
          setHasMobile(true);
        } else if (user.phoneNumber) {
          setHasMobile(true);
        } else {
          setHasMobile(false);
        }

        setUserName(profile?.name || user.displayName || null);

        const pic = profile?.picture || user.photoURL;
        setPicture(pic || undefined);

        const xp = profile?.xp || 0;
        let rankIndex = 0;
        for (let i = RANK_XP_THRESHOLDS.length - 1; i >= 0; i--) {
          if (xp >= RANK_XP_THRESHOLDS[i]) {
            rankIndex = i;
            break;
          }
        }
        setRankFrame(`/assets/rank/${rankIndex + 1}.webp`);
      } catch (e: any) {
        console.warn("[AUTH] Profile load error:", e);
        setAuthError(`Profile Load Error: ${e.message || "Unknown error"}`);
      } finally {
        isLoadingProfile.current = false;
        setFirebaseLoaded(true);
      }
    },
    [logout],
  );

  /**
   * Check if the URL hash contains Firebase OAuth response params.
   * If the hash has them but getRedirectResult returned null (iOS IndexedDB issue),
   * the redirect sign-in result was lost and we should notify the user.
   */
  const detectLostRedirect = useCallback((): boolean => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash;
    return (
      hash.includes("access_token") ||
      hash.includes("id_token") ||
      (hash.includes("state") && hash.includes("firebase"))
    );
  }, []);

  // Subscribe to Firebase auth state changes - the single source of truth
  const hasCheckedRedirect = useRef(false);

  useEffect(() => {
    let unmounted = false;
    let unsubscribe: (() => void) | undefined;
    let initStarted = false;
    let safetyTimer: ReturnType<typeof setTimeout> | undefined;

    const initFirebase = async () => {
      if (initStarted) return;
      initStarted = true;
      try {
        const { getRedirectResult, onAuthStateChanged } =
          await import("firebase/auth");
        const { auth } = await import("@/lib/firebase");

        const initAuth = async () => {
          if (hasCheckedRedirect.current || isRedirectChecking.current) return;
          isRedirectChecking.current = true;
          hasCheckedRedirect.current = true;

          try {
            const result = await getRedirectResult(auth);
            if (result?.user && !unmounted) {
              setFirebaseUser(result.user);
              firebaseUserRef.current = result.user;
            }
          } catch (err: any) {
            console.error("[AUTH] Redirect result error:", err);
            setAuthError(
              `Redirect sign-in failed: ${err.message || "Unknown error"}`,
            );
          } finally {
            isRedirectChecking.current = false;
            if (!unmounted) setAuthInitialized(true);
          }
        };

        await initAuth();

        if (unmounted) return;

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (unmounted) return;

          if (user) {
            setFirebaseUser(user);
            firebaseUserRef.current = user;
            if (user.photoURL) setPicture(user.photoURL);
            setAuthError(null);
            if (typeof window !== "undefined") {
              const isSecure = window.location.protocol === "https:";
              document.cookie = `auth_session=true; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax${isSecure ? "; Secure" : ""}`;
            }
            await loadUserProfile(user);
          } else {
            setFirebaseUser(null);
            firebaseUserRef.current = null;
            setUserGroups([]);
            setPicture(undefined);
            setUserName(null);
            setHasMobile(null);
            setRankFrame("/assets/rank/1.webp");
            if (authInitialized || isRedirectChecking.current === false) {
              setFirebaseLoaded(true);
            }
          }
        });
      } catch (err) {
        console.error("[AUTH] Failed to dynamically load Firebase Auth:", err);
        if (!unmounted) setFirebaseLoaded(true);
      }
    };

    const startFirebase = () => {
      initFirebase();
      // Safety timeout: never leave auth-dependent controls waiting forever.
      safetyTimer = setTimeout(() => {
        if (!unmounted) setFirebaseLoaded(true);
      }, 5000);
    };

    const hasSessionCookie = document.cookie
      .split("; ")
      .some((cookie) => cookie.startsWith("auth_session="));
    const hasAuthCallback =
      window.location.search.includes("login=true") || detectLostRedirect();

    // Anonymous visitors do not need the Firebase SDK during initial paint.
    // LoginModal dispatches this event when the visitor asks to sign in.
    if (hasSessionCookie || hasAuthCallback) startFirebase();
    else
      window.addEventListener("baasil:auth-needed", startFirebase, {
        once: true,
      });

    return () => {
      unmounted = true;
      window.removeEventListener("baasil:auth-needed", startFirebase);
      if (safetyTimer) clearTimeout(safetyTimer);
      if (unsubscribe) unsubscribe();
    };
  }, [loadUserProfile, authInitialized, detectLostRedirect]);

  // If redirect check finished but onAuthStateChanged didn't set loaded yet,
  // we do a secondary check here.
  useEffect(() => {
    if (authInitialized && !firebaseUser && !isRedirectChecking.current) {
      const isIOS =
        typeof window !== "undefined" &&
        /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const timer = setTimeout(
        () => {
          if (!firebaseUser) {
            // On iOS, check if there's a "lost redirect" - the URL has Firebase
            // OAuth hash params but getRedirectResult couldn't process them
            // (likely due to IndexedDB being unavailable). Surface this as an
            // error so the user knows the sign-in failed and can retry.
            if (isIOS && detectLostRedirect()) {
              setAuthError(
                "Sign-in result was lost during redirect (common on iOS Safari). " +
                  "Please try again - a popup will be used instead.",
              );
            }
            setFirebaseLoaded(true);
          }
        },
        isIOS ? 3000 : 500,
      ); // Increased grace period for iOS/iPhone
      return () => clearTimeout(timer);
    }
  }, [authInitialized, firebaseUser, detectLostRedirect]);

  // Periodic token refresh check every 5 minutes
  useEffect(() => {
    const interval = setInterval(
      async () => {
        if (firebaseUser) {
          await loadUserProfile(firebaseUser);
        }
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [firebaseUser, loadUserProfile]);

  const refreshSession = useCallback(async () => {
    const user = firebaseUserRef.current;
    if (user) {
      await loadUserProfile(user, true);
    }
  }, [loadUserProfile]);

  const updatePicture = useCallback((url: string) => {
    setPicture(url);
  }, []);

  /**
   * Returns a fresh Firebase ID token for use in API Authorization headers.
   * Firebase SDK auto-refreshes the token when it's within 5 minutes of expiry.
   */
  const getToken = useCallback(async (): Promise<string> => {
    const user = firebaseUserRef.current;
    if (!user) {
      throw new Error("No authenticated user");
    }
    return user.getIdToken();
  }, []);

  const allGroups = useMemo(
    () => Array.from(new Set(userGroups)).map((g) => g.toLowerCase()),
    [userGroups],
  );

  const isAdmin =
    allGroups.includes("admin") ||
    allGroups.includes("superadmin") ||
    allGroups.includes("super_admin") ||
    allGroups.includes("super-admin") ||
    allGroups.includes("support");
  const isSuperAdmin =
    allGroups.includes("superadmin") ||
    allGroups.includes("super_admin") ||
    allGroups.includes("super-admin");
  const isTopup = allGroups.includes("rgg-subscriber");
  const isSubscriber = isTopup;
  const isAuthenticated = !!firebaseUser;
  const isAuthLoading = !firebaseLoaded;

  const displayName =
    userName ||
    firebaseUser?.displayName ||
    firebaseUser?.email?.split("@")[0] ||
    "Player";

  const email = firebaseUser?.email ?? undefined;

  const value: AppAuthState = useMemo(
    () => ({
      userGroups: allGroups,
      isAdmin,
      isSuperAdmin,
      isTopup,
      isSubscriber,
      isAuthenticated,
      isAuthLoading,
      authError,
      displayName,
      email,
      hasMobile,
      picture,
      rankFrame,
      oidcAuth: {}, // kept for backward compat
      firebaseUser,
      logout,
      getToken,
      refreshSession,
      updatePicture,
    }),
    [
      allGroups,
      isAdmin,
      isSuperAdmin,
      isTopup,
      isSubscriber,
      isAuthenticated,
      isAuthLoading,
      authError,
      displayName,
      email,
      hasMobile,
      picture,
      rankFrame,
      firebaseUser,
      logout,
      getToken,
      refreshSession,
      updatePicture,
    ],
  );

  return (
    <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
  );
}
